import { useRenderNotifier } from '@wui/common-library'
import { useIM } from '@wui/im'

import { eventBus } from '@/entities/webrtc'

import { onPauseAllChatMessagePlayers, pauseAllChatMessagePlayers } from '@/shared/components/players'
import { useLocalization } from '@/shared/composables/localization'

import { clampVoicePlaybackRate } from '../lib/clamp-voice-playback-rate'
import { collectVoicePlaylist } from '../lib/collect-voice-playlist'
import { getCachedVoiceDurationSec } from '../lib/extract-audio-peaks'
import { getPlayableVoiceObjectUrl, revokePlayableVoiceObjectUrls } from '../lib/get-playable-voice-src'

import { useVoiceMessageStore } from './store'
import { VoicePlayTrack } from './types'

let audio: HTMLAudioElement | null = null
let listenersBound = false
let extraPauserBound = false
let suppressExtraPause = false
let loadedFileCookie = ''
let progressRaf = 0
const knownDurations = new Map<string, number>()

const getAudio = (): HTMLAudioElement => {
  if (audio) return audio

  audio = new Audio()
  return audio
}

const resolveDuration = (element: HTMLAudioElement, fallback = 0): number => {
  if (Number.isFinite(element.duration) && element.duration > 0) return element.duration
  return fallback > 0 ? fallback : 0
}

const rememberDuration = (fileCookie: string, duration: number) => {
  if (duration > 0) knownDurations.set(fileCookie, duration)
}

const fallbackDurationFor = (fileCookie: string, fallback = 0): number => {
  return fallback || knownDurations.get(fileCookie) || getCachedVoiceDurationSec(fileCookie)
}

const stopProgressLoop = () => {
  if (!progressRaf) return
  cancelAnimationFrame(progressRaf)
  progressRaf = 0
}

const startProgressLoop = () => {
  if (progressRaf) return

  const tick = () => {
    if (!audio || audio.paused || audio.ended) {
      progressRaf = 0
      return
    }
    syncFromAudio()
    progressRaf = requestAnimationFrame(tick)
  }

  progressRaf = requestAnimationFrame(tick)
}

const syncFromAudio = () => {
  if (!audio) return

  const store = useVoiceMessageStore()
  const current = store.playback
  if (!current) return

  const duration = resolveDuration(audio, fallbackDurationFor(current.fileCookie, current.duration))
  rememberDuration(current.fileCookie, duration)

  store.setPlayback({
    ...current,
    isPlaying: !audio.paused,
    currentTime: audio.currentTime || 0,
    duration,
  })
}

export const useVoicePlayer = () => {
  const store = useVoiceMessageStore()
  const { t } = useLocalization()
  const { notifyError } = useRenderNotifier()

  const applyAudioSettings = () => {
    const element = getAudio()
    element.playbackRate = store.playbackRate
    element.volume = store.volume
  }

  const pause = () => {
    stopProgressLoop()
    getAudio().pause()
    syncFromAudio()
  }

  const close = () => {
    stopProgressLoop()
    const element = getAudio()
    element.pause()
    element.removeAttribute('src')
    element.load()
    loadedFileCookie = ''
    revokePlayableVoiceObjectUrls()
    useVoiceMessageStore().setPlayback(null)
  }

  const closeIfCurrentEvent = (eventId?: number) => {
    if (!eventId || useVoiceMessageStore().playback?.eventId !== eventId) return
    close()
  }

  const playElement = async (element: HTMLAudioElement) => {
    try {
      await element.play()
      syncFromAudio()
    } catch (error) {
      const current = store.playback
      if (current) {
        store.setPlayback({ ...current, isPlaying: false })
      }

      if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) {
        return
      }

      console.error(error)
      notifyError(t('chat.voiceMessagePlayFailed'))
    }
  }

  const playTrack = async (track: VoicePlayTrack, startAt = 0, fallbackDuration = 0) => {
    suppressExtraPause = true
    pauseAllChatMessagePlayers()
    suppressExtraPause = false
    applyAudioSettings()

    const element = getAudio()
    try {
      const playableSrc = await getPlayableVoiceObjectUrl(track.fileCookie)
      if (loadedFileCookie !== track.fileCookie) {
        element.src = playableSrc
        loadedFileCookie = track.fileCookie
      }
    } catch (error) {
      console.error(error)
      notifyError(t('chat.voiceMessagePlayFailed'))
      return
    }

    const duration = resolveDuration(element, fallbackDurationFor(track.fileCookie, fallbackDuration))
    rememberDuration(track.fileCookie, duration)
    store.setPlayback({
      ...track,
      isPlaying: true,
      currentTime: startAt,
      duration,
    })

    const applyStart = () => {
      if (!startAt) return
      element.currentTime = startAt
    }

    applyStart()
    if (startAt && !(Number.isFinite(element.duration) && element.duration > 0)) {
      element.addEventListener('loadedmetadata', applyStart, { once: true })
    }

    await playElement(element)
  }

  const toggle = async (track: VoicePlayTrack, fallbackDuration = 0) => {
    const current = store.playback
    const element = getAudio()

    if (current?.fileCookie === track.fileCookie && loadedFileCookie === track.fileCookie) {
      if (element.paused) {
        suppressExtraPause = true
        pauseAllChatMessagePlayers()
        suppressExtraPause = false
        applyAudioSettings()
        await playElement(element)
        return
      }

      pause()
      return
    }

    await playTrack(track, 0, fallbackDuration)
  }

  const seek = (ratio: number, fallbackDuration = 0) => {
    const element = getAudio()
    const clamped = Math.min(1, Math.max(0, ratio))
    const current = store.playback
    const apply = () => {
      const duration = resolveDuration(
        element,
        fallbackDurationFor(current?.fileCookie ?? '', fallbackDuration || current?.duration || 0),
      )
      if (!duration) return false
      element.currentTime = duration * clamped
      syncFromAudio()
      return true
    }

    if (apply()) return

    element.addEventListener('loadedmetadata', () => {
      apply()
    }, { once: true })
  }

  const playlist = () => {
    const pServed = store.playback?.pServed
    if (!pServed) return []
    return collectVoicePlaylist(pServed, t('common.you'))
  }

  const adjacentTrack = (offset: number): VoicePlayTrack | undefined => {
    const current = store.playback
    if (!current) return undefined

    const tracks = playlist()
    const index = tracks.findIndex((track) => track.eventId === current.eventId)
    if (index < 0) return undefined

    return tracks[index + offset]
  }

  const playAdjacent = async (offset: number) => {
    const track = adjacentTrack(offset)
    if (!track) return
    await playTrack(track)
  }

  const setPlaybackRate = (rate: number) => {
    const nextRate = clampVoicePlaybackRate(rate)
    store.setPlaybackRate(nextRate)
    getAudio().playbackRate = nextRate
  }

  const setVolume = (volume: number) => {
    const nextVolume = Math.min(1, Math.max(0, volume))
    store.setVolume(nextVolume)
    getAudio().volume = nextVolume
  }

  if (!listenersBound) {
    listenersBound = true
    const element = getAudio()

    element.addEventListener('timeupdate', syncFromAudio)
    element.addEventListener('loadedmetadata', syncFromAudio)
    element.addEventListener('durationchange', syncFromAudio)
    element.addEventListener('pause', () => {
      stopProgressLoop()
      syncFromAudio()
    })
    element.addEventListener('play', () => {
      syncFromAudio()
      startProgressLoop()
    })
    element.addEventListener('ended', () => {
      stopProgressLoop()
      const next = adjacentTrack(1)
      if (next) {
        void playTrack(next)
        return
      }
      syncFromAudio()
    })

    eventBus.on('incomingCall', pause)

    const { onDeleteMessage } = useIM()
    onDeleteMessage((event) => {
      closeIfCurrentEvent(event.targetEventId)
    })
  }

  if (!extraPauserBound) {
    extraPauserBound = true
    onPauseAllChatMessagePlayers(() => {
      if (suppressExtraPause) return
      pause()
    })
  }

  applyAudioSettings()

  return {
    toggle,
    playTrack,
    pause,
    close,
    closeIfCurrentEvent,
    seek,
    playAdjacent,
    adjacentTrack,
    setPlaybackRate,
    setVolume,
  }
}

export const closeVoicePlaybackIfCurrentEvent = (eventId?: number) => {
  useVoicePlayer().closeIfCurrentEvent(eventId)
}
